/**
 * La catégorie d'un article : une seule règle, pour toutes les surfaces.
 *
 * ## Pourquoi ce fichier
 *
 * Trois endroits nommaient « la » catégorie d'un article, chacun avec sa règle,
 * et le même article s'annonçait donc différemment selon l'endroit où on le
 * regardait :
 *
 * | Surface | Ancienne règle | Résultat sur l'article des commissions Apple |
 * |---|---|---|
 * | Miniature dessinée | la 1re de la relation en base | Développement |
 * | Fil d'Ariane et JSON-LD | un ordre global écrit en dur | Entrepreneurs |
 * | Bloc du bas de page | toutes, ordre de la base | Développement, Entrepreneuriat, Startup, No-Code |
 *
 * Aucune des trois ne permettait de dire « celui-ci est d'abord un article
 * no-code » : l'ordre de la relation vient du modèle rédacteur et n'était
 * modifiable nulle part, et `Article::$categories` est une ManyToMany SANS
 * `OrderBy` côté Doctrine, donc cet ordre n'est même pas garanti d'une requête
 * à l'autre.
 *
 * ## La règle
 *
 * 1. `article.mainCategory`, si elle est posée ET que l'article la porte
 *    vraiment. C'est une désignation humaine, elle gagne sur tout le reste.
 * 2. Sinon, `CATEGORY_PREFERENCE`, l'ordre global d'avant. C'est ce repli qui
 *    rend le déploiement neutre : les articles déjà en ligne n'ont pas de
 *    principale, ils gardent donc exactement le fil d'Ariane qu'ils avaient.
 *
 * ## Ce qui ne change pas, et c'est volontaire
 *
 * **L'ENSEMBLE des chemins déclarés à Google reste le même.** Une page d'article
 * déclare un `BreadcrumbList` par catégorie filtrable, tous dans le même
 * `@graph` : la principale ne fait que passer en tête de la liste, elle n'en
 * retire aucun. Un article déjà indexé sous « Développeurs » pour une requête
 * de développeur continue donc de proposer ce chemin à Google, qui retient
 * celui qui colle à la requête. Seul le fil VISIBLE, celui qu'un lecteur suit,
 * change de catégorie. Ne jamais « simplifier » ce fichier en ne déclarant que
 * la principale : ce serait retirer des chemins déjà connus de Google.
 */

import { canonicalCategoryKey } from "@/helpers/categoryKey";

/**
 * Catégories qui ont un filtre sur /articles, DANS L'ORDRE DE PRÉFÉRENCE.
 *
 * Deux règles en une seule liste :
 *
 * 1. Seules ces catégories donnent un maillon. Le fil d'Ariane doit mener à une
 *    liste que le lecteur peut retrouver depuis la page ; une catégorie sans
 *    filtre (« startup », « designers »…) n'en mérite donc pas.
 * 2. L'ordre range les chemins déclarés, et désigne le fil visible des articles
 *    qui n'ont PAS de principale. Il l'emporte sur l'ordre de la base. Sans
 *    cela, mesuré sur 90 articles en ligne, 73 affichaient « Développeurs »
 *    dans les résultats de recherche et « No-code » aucun : la catégorie
 *    historique sortait en tête par inertie, et le fil d'Ariane racontait un
 *    site pour développeurs.
 *
 * Ce n'est donc PAS l'ordre d'affichage des filtres de
 * components/ArticlesCategoryFilter.jsx, seulement le même ensemble.
 *
 * Liste volontairement recopiée plutôt qu'importée du composant : celui-ci est
 * client, et l'import inverse y ferait entrer ce module de résolution sans
 * raison.
 */
export const CATEGORY_PREFERENCE = ["entrepreneurs", "nocode", "developers"];

/**
 * Noms de catégorie portés par un article, dans l'ordre où le back les rend.
 *
 * `categories` est la relation vivante. `category` est la colonne héritée,
 * encore seule sur les articles d'avant le classement automatique. Certains
 * endpoints la renvoient en texte, d'autres en objet, d'où les deux formes
 * acceptées.
 */
function categoryNames(article) {
    const names = [];

    for (const category of article?.categories || []) {
        if (typeof category?.name === "string") {
            names.push(category.name);
        }
    }

    const legacy = article?.category;
    if (typeof legacy === "string") {
        names.push(legacy);
    } else if (typeof legacy?.name === "string") {
        names.push(legacy.name);
    }

    return names;
}

/**
 * La clé de filtre désignée à la main sur l'article, si elle tient.
 *
 * Deux conditions, et la seconde n'est pas du zèle : une principale doit être
 * une catégorie que l'article PORTE, sinon le fil d'Ariane mènerait à une liste
 * où l'article ne figure pas. Le back applique déjà ce contrôle à l'écriture ;
 * il est refait ici parce que les catégories d'un article peuvent bouger
 * après coup, dans l'espace rédacteur, sans repasser par cette route.
 *
 * @returns {string|null} Clé canonique, ou null s'il n'y en a pas d'utilisable.
 */
function designatedKey(article) {
    const posee = canonicalCategoryKey(article?.mainCategory);
    if (!posee || !CATEGORY_PREFERENCE.includes(posee)) return null;

    const portees = categoryNames(article).map(canonicalCategoryKey);

    return portees.includes(posee) ? posee : null;
}

/**
 * Toutes les catégories filtrables d'un article, la principale en tête.
 *
 * Une par chemin d'Ariane à déclarer. La comparaison passe par
 * canonicalCategoryKey : c'est ce qui fait qu'un article rangé sous
 * « no-code » retrouve le filtre « nocode », resté écrit ainsi côté front
 * après la fusion des deux graphies.
 *
 * @param {object} article Article normalisé par le back
 * @returns {string[]} Clés de filtre. La principale d'abord si elle existe,
 *          puis les autres dans l'ordre de CATEGORY_PREFERENCE. Vide quand
 *          l'article n'en porte aucune, ce qui est un cas courant.
 */
export function resolveArticleCategoryKeys(article) {
    const portees = categoryNames(article).map(canonicalCategoryKey);
    const filtrables = CATEGORY_PREFERENCE.filter((key) =>
        portees.includes(key),
    );

    const principale = designatedKey(article);
    if (!principale) return filtrables;

    // La principale passe devant, les autres gardent l'ordre global. On ne perd
    // donc aucun chemin : c'est exactement la même liste, réordonnée.
    return [principale, ...filtrables.filter((key) => key !== principale)];
}

/**
 * La seule catégorie que portent le fil d'Ariane visible et la miniature.
 *
 * @param {object} article Article normalisé par le back
 * @returns {string|null} Clé du filtre, ou null si l'article n'en porte aucune.
 */
export function resolveArticleCategoryKey(article) {
    return resolveArticleCategoryKeys(article)[0] ?? null;
}

/**
 * La clé de traduction du libellé d'une catégorie.
 *
 * Un seul jeu de libellés sur tout le site : celui des filtres
 * (`courses_category_*`). Deux coexistaient, et la même catégorie se disait
 * « Développeurs » dans le fil d'Ariane et « Développement » en bas de
 * l'article. Le lecteur qui clique doit retrouver le mot du bouton sur lequel
 * il atterrit.
 *
 * Les replis restent nécessaires : seules les trois catégories filtrables ont
 * une clé `courses_category_*`, et les deux autres surfaces affichent aussi ce
 * qui n'a pas de filtre. Le bas de page montre « designers » ou « startup »
 * (`translateCategories_*`), et une miniature peut porter une rubrique qui
 * n'est pas une catégorie du tout, « believemy » ou « javascript »
 * (`thumbnail_rubrique_*`).
 *
 * @param {string} name Nom de la catégorie tel que le back le rend
 * @returns {{filtre: string, sujet: string, vignette: string}} Les clés à
 *          tenter. `filtre` d'abord, toujours ; l'appelant choisit son repli.
 */
export function categoryLabelKeys(name) {
    const key = canonicalCategoryKey(name);

    return {
        filtre: `courses_category_${key}`,
        sujet: `translateCategories_${key}`,
        vignette: `thumbnail_rubrique_${key}`,
    };
}

/**
 * La rubrique brute d'un article, telle qu'elle sera dessinée.
 *
 * Elle passe par le MÊME résolveur que le fil d'Ariane et que le bloc de
 * catégories du bas de page (helpers/articleMainCategory.js). Avant, elle
 * lisait la première ligne de la relation en base, et cette ligne n'est pas une
 * décision : elle vient de l'ordre où le modèle rédacteur a rendu ses
 * catégories, elle n'était modifiable nulle part, et `Article::$categories` est
 * une ManyToMany sans `OrderBy`, donc cet ordre n'est même pas stable. Un
 * article rangé « no-code, entrepreneurs, developers » pouvait ainsi porter
 * « DÉVELOPPEMENT » sur sa vignette pendant que son fil d'Ariane annonçait
 * « Entrepreneurs ».
 *
 * Le résolveur ne rend que les catégories qui ont un filtre sur /articles.
 * C'est voulu jusqu'ici : une vignette annonce une rubrique du blog, et une
 * catégorie sans filtre (« startup ») n'en est pas une. Le repli sur la colonne
 * héritée reste en dernier recours, pour les articles antérieurs au classement
 * automatique qui ne portent qu'elle.
 *
 * `article.category` est une COLONNE DE TYPE TEXTE côté Symfony, pas un objet.
 * La route publique lisait `article.category?.name`, ce qui vaut toujours
 * `undefined` : la branche était morte depuis le premier jour.
 */
export function rubriqueBrute(article) {
    const cle = resolveArticleCategoryKey(article);
    if (cle) {
        // Le nom porté par l'article et non la clé canonique : « no-code » se
        // dessine avec son tiret, et c'est ce nom que nomDeRubrique traduit.
        const nom = nomPorte(article, cle);
        if (nom) return nom;
    }

    const relation = article?.categories?.[0]?.name;
    if (typeof relation === "string" && relation.trim()) return relation;

    return typeof article?.category === "string" ? article.category : "";
}

/**
 * Le nom, tel qu'il est écrit en base, de la catégorie désignée par une clé.
 *
 * Le résolveur travaille sur des clés canoniques (« nocode ») parce que c'est
 * ainsi que les filtres du front sont écrits ; la miniature, elle, doit partir
 * du nom réel pour trouver sa traduction et pour avoir un repli lisible.
 */
function nomPorte(article, cle) {
    for (const categorie of article?.categories || []) {
        if (
            typeof categorie?.name === "string" &&
            canonicalCategoryKey(categorie.name) === cle
        ) {
            return categorie.name;
        }
    }

    const legacy = article?.category;
    if (typeof legacy === "string" && canonicalCategoryKey(legacy) === cle) {
        return legacy;
    }

    return "";
}
